import React from 'react';

// Identical mic glyph to AriaDock / SetupHelpBox so the input row matches.
const MicIcon = ({ on }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.6 : 2.2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 19v3" />
  </svg>
);

// AssistChatBox — the Assist-mode panel, skinned to look EXACTLY like the
// support chat (owner request): dot + "Aria" + subtitle + × header, a greeting
// bubble, iMessage-style thread, and the pill input + round send button. It
// reuses the .support-chat* classes so the look is identical; the only
// difference (`.support-chat--assist`) makes it fill the whole compact Assist
// window instead of floating in a corner.
//
// Unlike the standalone SupportChat, this box drives the REAL Assist pipeline:
// the input is wired to the app's `input` + `sendMessage` (so Aria actually
// controls the computer), the thread shows the live conversation `messages`,
// and the × stops Assist.
export default function AssistChatBox({ messages, input, onInputChange, onSend, busy, status, onStop, micOn, onToggleMic, onPlus = null }) {
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!String(input || '').trim() || busy) return;
    onSend();
  };

  // VOICE TURNS DON'T RENDER AS TEXT (Braeden 8/29): "there should only be
  // the talking circle ... text box is only for the text communicaiton, not
  // the tts talking." The TalkingBall above this panel IS the spoken
  // conversation; this thread keeps only the typed exchanges. Untagged
  // messages still show — hiding them would eat replies from any append site
  // that forgot the mode tag, and a silently missing reply is the exact bug
  // this panel exists to prevent (see the 8/25 relay note in App.jsx).
  const list = Array.isArray(messages)
    ? messages.filter((m) => m && typeof m.content === 'string' && m.content.trim() && m.mode !== 'voice')
    : [];

  return (
    <div className="support-chat support-chat--assist" role="dialog" aria-label="Aria Assist">
      <div className="support-chat__header support-chat__header--centered">
        <div className="support-chat__script">Aria</div>
        <button
          type="button"
          className="support-chat__close"
          onClick={onStop}
          aria-label="Stop Assist"
          title="Stop Assist"
        >
          ×
        </button>
      </div>

      <div className="support-chat__body" ref={scrollRef}>
        {list.length === 0 && (
          <div className="support-chat__bubble support-chat__bubble--assistant">
            Assist is on — I can see your screen. Tell me what to do and I&rsquo;ll handle it.
          </div>
        )}
        {list.map((m, i) => (
          <div
            key={i}
            className={`support-chat__bubble support-chat__bubble--${m.role === 'user' ? 'user' : 'assistant'}`}
          >
            {typeof m.content === 'string' ? m.content : String(m.content)}
          </div>
        ))}
        {busy && (
          <div className="support-chat__bubble support-chat__bubble--assistant support-chat__typing">
            <span /><span /><span />
          </div>
        )}
      </div>

      <div className="support-chat__input-row">
        <button type="button" onClick={onToggleMic}
          aria-label={micOn ? 'Microphone on — tap to mute' : 'Tap to talk'}
          style={{ width: 48, height: 48, borderRadius: '50%', background: micOn ? '#ff453a' : '#303744', color: '#fff', border: 0 }}>
          <MicIcon on={micOn} />
        </button>
        <span role="status">{micOn ? 'Listening — speak to Aria' : 'Tap the microphone to speak'}</span>
      </div>
    </div>
  );
}
